import {
  type AuthError,
  authErrorSchema,
  CSRF_HEADER,
  type SignInStep,
  signInStepSchema,
} from "@esi/shared";
import { api } from "../api.ts";

/**
 * The pre-session flow of this tab (sign-in, invitation and reset pages): the server binds it
 * to the browser with an httpOnly cookie, and its synchroniser token stays in memory here,
 * sent with every pre-session POST. Never stored.
 */
let csrfToken: string | null = null;
/** The flow being started: concurrent starts share it (the cookie and token stay a pair). */
let starting: Promise<void> | null = null;

/**
 * Starts a new flow (on page load, and again after one expired), on the tenant application's
 * sign-in or, on the console website, the operators' (`operator`).
 */
export function startFlow(realm: "tenant" | "operator" = "tenant"): Promise<void> {
  // Two overlapping starts (React's development double effects) would set two flow cookies,
  // and the token kept could be the other flow's: every step would then answer `409`.
  starting ??= (async () => {
    try {
      const res = await (realm === "operator"
        ? api.api.auth.operator.csrf.$post()
        : api.api.auth.csrf.$post());
      csrfToken = res.ok ? (await res.json()).csrfToken : null;
    } finally {
      starting = null;
    }
  })();
  return starting;
}

/** Request options of a pre-session POST: the flow's CSRF header. */
export const flowHeaders = () => ({ headers: { [CSRF_HEADER]: csrfToken ?? "" } });

/**
 * Sends a pre-session POST; when the flow expired meanwhile (10 minutes, `409`), starts a new
 * one and sends it once more. For pages that keep their own state (a link's token).
 */
export async function withFlow<R extends { status: number }>(send: () => Promise<R>): Promise<R> {
  const res = await send();
  if (res.status !== 409) {
    return res;
  }
  await startFlow();
  return send();
}

/** For tests: forget the flow. */
export const resetFlow = () => {
  csrfToken = null;
  starting = null;
};

export type StepResult =
  | { ok: true; step: SignInStep }
  | { ok: false; status: number; error: AuthError | null };

/** Reads a sign-in answer: the next step, or a coded refusal. */
export async function readStep(res: { ok: boolean; status: number; json(): Promise<unknown> }) {
  const body: unknown = await res.json().catch(() => null);
  if (res.ok) {
    const step = signInStepSchema.safeParse(body);
    if (step.success) {
      return { ok: true, step: step.data } satisfies StepResult;
    }
  }
  const error = authErrorSchema.safeParse(body);
  return {
    ok: false,
    status: res.status,
    error: error.success ? error.data : null,
  } satisfies StepResult as StepResult;
}

/** Minutes to wait, rounded up, from a throttled answer (`retryAfter` seconds). */
export const retryMinutes = (error: AuthError | null) =>
  Math.max(1, Math.ceil((error?.retryAfter ?? 60) / 60));
